import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { i18n, TFunction } from "i18next";
import { ChangeEvent, useMemo, useRef } from "react";
import AttentionIcon from "assets/Alerts/AttentionIcon.svg?url";
import SuccessIcon from "assets/Alerts/SuccessIcon.svg?url";
import ErrorIcon from "assets/Alerts/ErrorIcon.svg?url";
// import InfoIcon from 'assets/Alerts/InfoIcon.svg?url';
import UpdateIcon from "assets/Alerts/update-ico.svg?url";
import { t } from "i18next";
import Checkbox from "@mui/material/Checkbox";
import { NavigateFunction } from "react-router-dom";

interface NotificationItemProps {
  data: any;
  themeColors: any;
  t: TFunction<"translation", undefined>;
  actions: Record<number, string>;
  types: Record<number, string>;
  pageNo: number;
  i18n: i18n;
  handleReadUnread: (row: any) => void;
  loading: boolean;
  navigate: NavigateFunction;
  closePopover: () => void;
}
const NotificationItem: React.FC<NotificationItemProps> = (
  props: NotificationItemProps,
) => {
  const rowsRef = useRef<any[]>([]);
  const isHiddenRolePrivilegeNotification = (row: any) => {
    const typeKey = String(props.types?.[row?.type] || "")
      .toUpperCase()
      .replace(/\s+/g, "_");
    if (typeKey !== "ROLE_PRIVILEGE") return false;
    const actionKey = String(props.actions?.[row?.action] || "").toUpperCase();
    return (
      !actionKey ||
      actionKey.includes("ASSIGN") ||
      actionKey.includes("UNASSIGN") ||
      [83, 84, 85].includes(Number(row?.action))
    );
  };
  const visibleRows = useMemo(() => {
    const newRows = (props?.data?.content || []).filter(
      (row: any) => !isHiddenRolePrivilegeNotification(row),
    );
    if (props.pageNo === 1) {
      rowsRef.current = newRows;
      return rowsRef.current;
    }
    rowsRef.current = rowsRef.current.map((old) => {
      const updated = newRows.find((r: any) => r.id === old.id);
      return updated ? updated : old;
    });

    // append truly new rows
    const existingIds = new Set(rowsRef.current.map((r) => r.id));
    const filteredRows = newRows.filter((r: any) => !existingIds.has(r.id));

    rowsRef.current = [...rowsRef.current, ...filteredRows];

    return rowsRef.current;
  }, [props.data, props.pageNo, props.types, props.actions]);
  const typeColors = {
    error: "#FFF5F3",
    success: "#F6FFF9",
    attention: "#FCFAFE",
    info: "#FFF8EC",
  };
  const actionLabelMap = {
    9: props.t("estimatePackage"),
    25: props.t("estimateService"),
    33: props.t("estimateItem"),
  };
  const getReferenceNumber = (type: number, attributes: any) => {
    switch (true) {
      case type >= 100 && type < 200: // Job Card
        return attributes?.jobCardNo || attributes?.estimationNo;
      case type >= 200 && type < 300: // Work Order
        return attributes?.workOrderNo;
      case type === 300: // Invoice
        return attributes?.invoiceNo;
      case type === 2100: // Invoice Payment
        return attributes?.invoiceNo;
      case type === 400: // Invoice
        return attributes?.paymentNo;
      case type === 500: // Invoice
        return attributes?.creditNoteNo;
      case type === 800: // Return Cash
        return attributes?.returnCashNo;
      case type === 700: // inspection
        return attributes?.workOrderNo || attributes?.jobCardNo;
      case type === 1900: // proforma
        return attributes?.proformaInvoiceNo || "";
      case type >= 600 && type < 700: // Cart
        return attributes?.cartNo || attributes?.id;
      case type === 2000: // Cart
        return attributes?.title || attributes?.id;
      case type === 1000: // Cart
        return attributes?.PoNo || "";
      case type === 1100: // Cart
        return attributes?.InvoiceNumber || "";
      case type === 1200: // Cart
        return attributes?.PaymentNumber || "";
      case type === 1301: // Cart
        return attributes?.itemCode || "";
      case type === 1300: // Cart
        return attributes?.StockNumber || "";
      case type === 1600: // PurchaseCreditDebitNote
        return attributes?.CreditDebitNoteNumber || "";
      default:
        return attributes?.itemId || attributes?.id;
    }
  };
  return (
    <>
      {!visibleRows || visibleRows.length === 0 ? (
        <Typography
          className="font-Saira italic font-[500] text-[14px] absolute left-[39%] top-[47%]"
          sx={{ color: props?.themeColors?.primary }}
        >
          {props.t("noRecordFound")}
        </Typography>
      ) : (
        visibleRows.map((row: any) => {
          const generateNotificationMsg = () => {
            const { type, action, attributes } = row;
            // STOCK LOW NOTIFICATION
            if (type === 1800 && action === 90) {
              const itemName = attributes?.itemName || "Item";
              const storeName = attributes?.storeName || "Store";
              return (
                <>
                  <strong>{itemName}</strong> {props.t("stockIsLowIn")}{" "}
                  <strong>{storeName}</strong>
                </>
              );
            }
            const userName =
              type === 2100
                ? attributes?.clientName
                : attributes?.createdByName || "System";

            const stationName = attributes?.stationName || "Unknown Station";
            const deliveryNoteNo = attributes?.deliveryNoteNo || "";
            const referenceNo = deliveryNoteNo
              ? deliveryNoteNo
              : getReferenceNumber(type, attributes) || "";
            const consolidatedInvoiceNo = attributes?.invoiceNo;

            /** SOS HANDLING */
            if (type === 900) {
              const driverName = attributes?.drivername || "Driver";
              const location = attributes?.location || "unknown location";
              const plateNumber = attributes?.plateNumber || "";
              const vehicleInfo = plateNumber || "";
              const message = attributes?.msg || "";
              const stationMsg = attributes?.stationMsg || "";

              switch (action) {
                case 1: // Created
                  return (
                    <>
                      {props.t("SOS")}! {props.t("createdAt")}{" "}
                      <strong>{location}</strong> {props.t("by")}{" "}
                      <strong>{driverName}</strong> {props.t("For")}{" "}
                      {props.t("plateNumber")} #<strong>{vehicleInfo}</strong>.{" "}
                      {props.t("Message")}: <strong>{message}</strong>
                    </>
                  );

                case 2: // Updated
                  return (
                    <>
                      {props.t("SOS")} {props.t("updated by")}{" "}
                      <strong>{userName}</strong> {props.t("at")}{" "}
                      <strong>{stationName}</strong>. {props.t("message")}:{" "}
                      <strong>{stationMsg}</strong>
                    </>
                  );

                case 3: // Resolved
                  return (
                    <>
                      SOS Resolved for <strong>{driverName}</strong>
                      <strong>{vehicleInfo}</strong> {props.t("at")}{" "}
                      <strong>{location}</strong>
                    </>
                  );

                default:
                  return (
                    <>
                      {props.t("SOS")}{" "}
                      {props.t(props.actions[action]) || "UPDATE"}{" "}
                      {props.t("for")} <strong>{driverName}</strong>
                      <strong>{vehicleInfo}</strong>
                    </>
                  );
              }
            }

            /** ENTITY NAME FUNCTION */
            const getEntityName = (t: any) => {
              if (t >= 100 && t < 200)
                return attributes?.estimationNo ? "Estimate" : "Job Card";
              if (t >= 200 && t < 300) return "Work Order";
              if (t === 300) return "INVOICE";
              if (t === 400) return "Payment";
              if (t === 500) return "CREDITNOTE";
              if (t >= 600 && t < 700) return "cart";
              if (t === 700 && attributes.workOrderNo) return "Work Order";
              if (t === 700 && attributes.jobCardNo) return "Job Card";
              if (t === 700) return "Vehicle Inspection";
              if (t === 800) return "Return Cash";
              if (t === 2000) return "BOOKING";
              if (t === 2100) return "INVOICE";
              if (t === 2300) return "consolidatedInvoice";
              return "";
            };

            const entityName = getEntityName(type);
            const serviceTitle =
              attributes?.title || attributes?.serviceCode || "";
            const isAddedAction = action === 33 || action === 9;
            const isRemovedAction = action === 36 || action === 12;

            const actionText = isAddedAction
              ? props.t("addedto")
              : isRemovedAction
                ? props.t("removedFrom")
                : props.t(props.actions[action]) || props.t("modified");

            /** MAIN SWITCH */
            switch (action) {
              /** CREATED */
              case 1:
                return (
                  <>
                    {deliveryNoteNo
                      ? props.t("deliveryNote")
                      : props.t(entityName)}{" "}
                    {type === 2000 ? "" : "#"}
                    <strong>{referenceNo || consolidatedInvoiceNo}</strong>{" "}
                    {attributes.proformaInvoiceNo
                      ? props.t("issuedAt")
                      : props.t("createdAt")}{" "}
                    <strong>{stationName}</strong>{" "}
                    {type === 2100 ? props.t("for") : props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** UPDATED */
              case 2: {
                const previousBay = attributes?.previousBay;
                const currentBay = attributes?.currentBay;

                const isBayNotification = previousBay || currentBay;

                if (
                  isBayNotification &&
                  (entityName === "Work Order" || entityName === "Job Card")
                ) {
                  return (
                    <>
                      {props.t("Bayupdatedfrom")}{" "}
                      <strong>{previousBay || "-"}</strong> {props.t("to")}{" "}
                      <strong>{currentBay || "-"}</strong> {props.t("by")}{" "}
                      <strong>{userName}</strong>
                    </>
                  );
                }

                return (
                  <>
                    {entityName} {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("updatedBy")}{" "}
                    <strong>{userName}</strong>
                  </>
                );
              }

              /** ARCHIVED */
              case 3:
                return (
                  <>
                    {entityName} {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("deletedBy")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** SERVICE CREATED */
              case 25:
              case 29:
                return (
                  <>
                    <strong>{serviceTitle}</strong> {props.t("addedto")}{" "}
                    <strong>{entityName}</strong> {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** SERVICE REMOVED */
              case 28:
              case 32:
                return (
                  <>
                    <strong>{serviceTitle}</strong> {props.t("removedFrom")}{" "}
                    <strong>{entityName}</strong> {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              case 60:
                return (
                  <>
                    {props.t("discountUpdatedFor")} {entityName}{" "}
                    {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** ESTIMATE CONVERTED TO JOB CARD */
              case 79:
                return (
                  <>
                    {props.t("estimateConvertedTo")} {entityName}{" "}
                    {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** VEHICLE INSPECTION CREATED */
              case 75:
                return (
                  <>
                    {props.t("vehicleInspectionFor")} {entityName}{" "}
                    {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("createdBy")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** VEHICLE INSPECTION DELETED */
              case 59:
                return (
                  <>
                    {props.t("vehicleInspectionFor")} {entityName}{" "}
                    {type === 2000 ? "" : "#"}
                    <strong>{referenceNo}</strong> {props.t("deletedBy")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              /** ESTIMATE UPDATED SERVICE DISCOUNT */
              case 67:
              case 63:
              case 61:
                return (
                  <>
                    {entityName}{" "}
                    {action === 67
                      ? props.t("UPDATE_SERVICE_DISCOUNT")
                      : action === 63
                        ? props.t("UPDATE_PACKAGE_DISCOUNT")
                        : props.t("UPDATE_ITEM_DISCOUNT")}{" "}
                    # <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              case 69:
              case 65:
              case 64:
                return (
                  <>
                    {entityName}{" "}
                    {action === 64
                      ? props.t("UPDATE_PACKAGE_ITEM_DISCOUNT")
                      : action === 65
                        ? props.t("UPDATE_PACKAGE_SERVICE_DISCOUNT")
                        : props.t("UPDATE_SERVICE_ITEM_DISCOUNT")}{" "}
                    # <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              case 80:
                return (
                  <>
                    {entityName} {props.t("UPLOAD_FILE")} #{" "}
                    <strong>{referenceNo}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );

              case 8:
                if (type === 2000) {
                  return (
                    <>
                      {entityName}{" "}
                      {attributes.status === "APPROVE"
                        ? props.t("approved")
                        : attributes.status}{" "}
                      {props.t("successfully")} {props.t("by")}{" "}
                      <strong>{userName}</strong>
                    </>
                  );
                }
                return (
                  <>
                    {props.t("Stocktransferredfrom")}{" "}
                    <strong>{attributes.SourceStoreTitle}</strong>{" "}
                    {props.t("to")}{" "}
                    <strong>{attributes.DestinationStoreTitle}</strong>{" "}
                    {props.t("by")} <strong>{userName}</strong>
                  </>
                );

              case 99:
                return (
                  <>
                    {entityName} {props.t("createsComment")}{" "}
                    <strong>{serviceTitle}</strong> {props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );
              default: {
                const previousBay = attributes?.previousBay;
                const currentBay = attributes?.currentBay;
                const isBayNotification = previousBay || currentBay;

                if (
                  isBayNotification &&
                  (entityName === "Work Order" || entityName === "Job Card")
                ) {
                  if (!previousBay) {
                    return (
                      <>
                        {props.t("Bay")} <strong>{currentBay || "-"}</strong>{" "}
                        {props.t("assignedTo")}{" "}
                        <strong>{referenceNo || "-"}</strong> {props.t("by")}{" "}
                        <strong>{userName}</strong>
                      </>
                    );
                  } else {
                    return (
                      <>
                        {props.t("Bayupdatedfrom")}{" "}
                        <strong>{previousBay || "-"}</strong> {props.t("to")}{" "}
                        <strong>{currentBay || "-"}</strong> {props.t("by")}{" "}
                        <strong>{userName}</strong>
                      </>
                    );
                  }
                }

                if (type === 2100) {
                  const invoiceNo = attributes?.invoiceNo || referenceNo;
                  const customer = attributes?.clientName || "Customer";
                  return (
                    <>
                      {props.t("PaymentforInvoice")} #
                      <strong>{invoiceNo}</strong> {props.t("ofCustomer")}{" "}
                      <strong>{customer}</strong> {props.t("isdue")}.
                    </>
                  );
                }

                return (
                  <>
                    {isAddedAction || isRemovedAction
                      ? serviceTitle
                      : entityName}{" "}
                    {actionText}{" "}
                    {isAddedAction || isRemovedAction ? entityName : ""}{" "}
                    {type === 2000 ? "" : "#"} <strong>{referenceNo}</strong>{" "}
                    {type === 2100 ? props.t("for") : props.t("by")}{" "}
                    <strong>{userName}</strong>
                  </>
                );
              }
            }
          };
          const getAlertType = () => {
            if ([3, 6, 12, 16, 20, 24, 28, 32, 36, 59].includes(row.action)) {
              return { type: "error", icon: ErrorIcon };
            }
            if ([1, 5, 9, 13, 17, 21, 25, 29, 33, 75].includes(row.action)) {
              return { type: "success", icon: SuccessIcon };
            }
            if (
              [2, 7, 8, 10, 14, 18, 22, 26, 30, 34, 57].includes(row.action)
            ) {
              return { type: "attention", icon: AttentionIcon };
            }
            return { type: "info", icon: UpdateIcon };
          };
          const { type, icon } = getAlertType();
          const handleClick = () => {
            const { type, typeId, attributes } = row;

            const getEntityId = () => {
              switch (true) {
                case type >= 100 && type < 200: // Job Card
                  return (
                    attributes?.jobCardId ||
                    // attributes?.jobCardNo ||
                    // attributes?.jobId ||
                    typeId
                  );
                case type >= 200 && type < 300: // Work Order
                  return attributes?.workOrderId || typeId;
                case type === 300 || type === 2100: // Invoice
                  return attributes?.invoiceId || typeId;
                case type === 500: // Invoice
                  return attributes?.invoiceId || typeId;
                case type === 400: // Payment
                  return attributes?.paymentId || typeId;
                case type === 2000: // Invoice
                  return attributes?.bookingId || typeId;
                case type === 700: // Inspection
                  return attributes?.inspectionFormId || typeId;
                case type >= 600 && type < 700: // Cart
                  return attributes?.cartId || typeId;
                case type === 1900: // Proforma
                  return attributes?.workOrderId || typeId;
                default:
                  return attributes?.itemId || typeId;
              }
            };
            const id = getEntityId();
            switch (true) {
              case type === 700: // Inspection with jobCardId
                if (attributes?.jobCardId) {
                  props.navigate(`/job/inspection/${id}`);
                } else if (attributes?.workOrderId) {
                  props.navigate(`/work/inspection/${id}`);
                }
                break;

              case type >= 100 && type < 150: // Job Card and sub-types
                props.navigate(`/job-card/${id}`);
                break;
              case type >= 150 && type < 200: // Estimates and sub-types
                props.navigate(`/estimate/${id}`);
                break;
              case type >= 200 && type < 300: // Work Order and sub-types
                props.navigate(`/work-order/${id}`);
                break;
              case type === 300 || type === 2100: // Invoice
                props.navigate(`/invoices/${id}`);
                break;
              case type === 500: // Invoice
                props.navigate(`/invoice/creditnotes/${id}`);
                break;
              case type === 400: // Payment
                props.navigate(`/payment/${id}`);
                break;
              case type === 800: // Return Cash
                props.navigate(`/return-cash`);
                break;
              case type === 2000: // Return Cash
                props.navigate(`/booking/view/${id}`);
                break;

              case type === 900: // SOS
                break;
              case type >= 600 && type < 700: // Cart and sub-types
                props.navigate(`/direct-sales/${id}/items`);
                break;
              case type === 1900: // Cart and sub-types
                props.navigate(`/profoma/print/${id}`);
                break;
              default:
                // No navigation for other types
                break;
            }
            if (!row?.read) {
              props.handleReadUnread(row);
            }
            setTimeout(() => props.closePopover(), 100);
          };
          return (
            <Card
              key={row?.id}
              sx={{
                boxShadow: "none",
                width: "100% !important",
                marginBottom: "10px",
                outline: row?.read ? "none" : "1px solid rgba(0,0,0,0.15)",
                background: (typeColors as any)[type],
                cursor: row?.read ? "auto" : "pointer",
                "& .notification-checkbox": { opacity: 0 },
                "&:hover .notification-checkbox": { opacity: 1 },
              }}
              onClick={() => {
                row?.action === 83 ||
                row?.action === 84 ||
                row?.action === 85 ||
                row?.type >= 500
                  ? null
                  : handleClick();
              }}
            >
              <CardContent className="flex gap-3 items-start p-[16px] font-Saira">
                <img src={icon} alt={type} width={30} height={30} />
                <Box className="w-[100%] mt-[-2px]">
                  <Box className="flex justify-between items-baseline gap-2">
                    <Box
                      className={`text-[13px] ${row?.read ? "font-medium" : "font-semibold"} mb-1`}
                    >
                      {row?.type === 100 && row?.attributes?.estimationNo
                        ? props.t("Estimate")
                        : row?.type === 2300
                          ? props.t("consolidatedInvoice")
                          : row?.attributes?.estimationNo
                            ? (actionLabelMap as any)[row?.action] ||
                              props.t("Estimate")
                            : props.t(props.types[row?.type]) ||
                              props.t("Notification")}
                    </Box>
                    <Box className="text-end">
                      <Tooltip
                        title={row?.read ? t("markAsUnread") : t("markAsRead")}
                      >
                        <Checkbox
                          className="notification-checkbox"
                          sx={{
                            padding: 0,
                            "& .MuiSvgIcon-root": {
                              color: props.themeColors.primary,
                            },
                          }}
                          checked={row?.read ? true : false}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            event.stopPropagation();
                            props.handleReadUnread(row);
                          }}
                          disabled={props.loading}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box className="flex justify-between items-baseline gap-2">
                    <Tooltip title={generateNotificationMsg()}>
                      <Box className="text-[10px] max-w-[70%]">
                        {generateNotificationMsg()}
                      </Box>
                    </Tooltip>
                    <Box className="text-[10px] text-end">
                      {row?.actionTime
                        ? (() => {
                            const d = new Date(row.actionTime);
                            const today = new Date();
                            const yesterday = new Date();
                            yesterday.setDate(today.getDate() - 1);

                            const isToday =
                              d.toDateString() === today.toDateString();
                            const isYesterday =
                              d.toDateString() === yesterday.toDateString();
                            return (
                              <span dir="ltr">
                                {`${
                                  isToday
                                    ? props.t("today")
                                    : isYesterday
                                      ? props.t("yesterday")
                                      : new Intl.DateTimeFormat(
                                          props.i18n.language,
                                          {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                          },
                                        ).format(d)
                                }  
                                                    ${new Intl.DateTimeFormat(
                                                      props.i18n.language,
                                                      {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        second: "2-digit",
                                                        hour12: true,
                                                        numberingSystem:
                                                          props.i18n
                                                            .language ===
                                                            "ur" ||
                                                          props.i18n
                                                            .language === "ar"
                                                            ? "arab"
                                                            : "latn",
                                                      },
                                                    ).format(d)}`}
                              </span>
                            );
                          })()
                        : null}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}
    </>
  );
};

export default NotificationItem;
